/**
 * Focus trap + keyboard navigation for the modal.
 */

export type KeyboardTrapCleanup = () => void;

export function setupKeyboardTrap(
  modalEl: HTMLElement,
  onEscape: () => void
): KeyboardTrapCleanup {
  const handler = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      onEscape();
      return;
    }

    if (e.key === "Tab") {
      const focusableElements = modalEl.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const focusable = Array.from(focusableElements).filter(
        (el) => !el.hasAttribute("disabled") && el.offsetParent !== null
      );

      if (focusable.length === 0) {
        e.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      // When the modal lives inside a shadow root, document.activeElement
      // returns the shadow host rather than the actually focused element.
      // Read activeElement from the modal's own root for a correct answer.
      const root = modalEl.getRootNode() as Document | ShadowRoot;
      const active =
        (root as ShadowRoot).activeElement ?? document.activeElement;

      if (e.shiftKey) {
        if (active === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
  };

  document.addEventListener("keydown", handler);

  // Auto-focus first focusable element
  requestAnimationFrame(() => {
    const firstFocusable = modalEl.querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    firstFocusable?.focus();
  });

  return () => {
    document.removeEventListener("keydown", handler);
  };
}
